// src/pages/CategoryProducts.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Search, SlidersHorizontal, Package, ArrowLeft, Home as HomeIcon } from 'lucide-react';
import ProductCard from '../components/ProductCard';

const CategoryProducts = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Convert URL slug to display name
  const categoryName = name
    ? name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : '';

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Fetch products matching this category
        const q = query(
          collection(db, 'products'),
          where('category', '==', categoryName)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(data);
        setFilteredProducts(data);
      } catch (error) {
        console.error('Error fetching category products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
    window.scrollTo(0, 0);
  }, [categoryName]);

  useEffect(() => {
    let result = [...products];
    if (searchTerm) {
      result = result.filter(p =>
        p.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (sortBy === 'low-high') result.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === 'high-low') result.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sortBy === 'popular') result.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
    else if (sortBy === 'newest') result.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setFilteredProducts(result);
  }, [searchTerm, sortBy, products]);

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)}
            className="p-2 rounded-full btn-2d"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs mb-1" style={{ color: 'var(--color-secondary-text)' }}>
              <HomeIcon size={12} />
              <Link to="/" className="hover:opacity-80">Home</Link>
              <span>/</span>
              <span>Category</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold truncate" style={{ color: 'var(--color-primary-text)' }}>
              {categoryName}
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
              {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} available
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-xl shadow-md p-4 mb-6 flex flex-col md:flex-row gap-3"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
          <div className="relative flex-1">
            <input type="text" placeholder="Search in this category..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg py-3 px-4 pl-11 text-sm focus:outline-none"
              style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }} />
            <Search className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
          </div>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="rounded-lg py-3 px-3 text-sm focus:outline-none cursor-pointer"
            style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}>
            <option value="newest">Newest First</option>
            <option value="low-high">Price: Low to High</option>
            <option value="high-low">Price: High to Low</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>

        {/* Products */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
              style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
            <p className="mt-4" style={{ color: 'var(--color-secondary-text)' }}>Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <Package size={64} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
            <p className="text-lg font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>
              Is category mein koi product nahi hai
            </p>
            <p className="text-sm mb-6" style={{ color: 'var(--color-secondary-text)' }}>
              {searchTerm ? `"${searchTerm}" ke liye kuch nahi mila.` : 'Admin panel se products add karein.'}
            </p>
            <Link to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold btn-2d shadow-lg"
              style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
              Browse All Products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((product, i) => (
              <div key={product.id} className="anim-fadeInUp" style={{ animationDelay: `${i * 50}ms` }}>
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}

        <div className="h-20 md:h-0"></div>
      </div>
    </div>
  );
};

export default CategoryProducts;